import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth, isAdminLike } from '../context/AuthContext';
import { styles, StatusPill, fmtDate, currentWeekOf, useIsMobile, mobile } from '../components/UI';
import EventModal from '../components/EventModal';
import Attachments from '../components/Attachments';
import FollowUpModal, { STATUSES, STATUSES_NEEDING_FOLLOWUP, CONTACT_TYPES } from '../components/FollowUpModal';

const blank = () => ({
  rep_id: null,
  week_of: currentWeekOf(),
  customer: '',
  contact_name: '',
  contact_title: '',
  contact_type: '',
  email: '',
  phone: '',
  notes: '',
  follow_up_date: '',
  follow_up_type: '',
  status: 'In the Hunt',  // start of the pipeline
  kind: ''                 // force pick (no auto-default -- reps were missing it)
});

// STATUSES / STATUSES_NEEDING_FOLLOWUP / CONTACT_TYPES moved to
// components/FollowUpModal.jsx (2026-07-07) so the Dashboard can share the
// follow-up flow -- imported above, same values.

// Friendly labels for project_events.field -- must match TRACKED_FIELDS
// keys in server/routes/projects.js.
const TRACKED_FIELDS_LABEL = {
  status:          'Status',
  estimated_value: 'Forecast $',
  actual_value:    'Actual $',
  win_pct:         'Win %',
  date_planned:    'Planned Date',
  site:            'Site',
  quarter:         'Quarter',
  po_number:       'PO #',
};

// Contact types where Contact Name + Title are not required (rep walked
// in without a known contact, etc).
const CONTACT_TYPES_NO_NAME_REQUIRED = new Set(['Cold Call']);

// Note templates double as type-setters: clicking one inserts the body into
// Notes AND sets contact_type so the entry is properly categorized.
const NOTE_TEMPLATES = [
  { type: 'Phone Call', label: '📞 Phone call', body: 'Phone call:\n- Discussed: \n- Outcome: \n- Next step: ' },
  { type: 'Meeting',    label: '🍽️ Meeting',    body: 'Meeting:\n- Topics: \n- Action items: \n- Next step: ' },
  { type: 'Site Visit', label: '🚗 Site visit', body: 'Site visit:\n- Saw: \n- Opportunity: \n- Next step: ' },
  { type: 'Email',      label: '📧 Email',      body: 'Email follow-up:\n- Re: \n- Sent / received: \n- Reply expected: ' }
];

export default function CallLog() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [projectEvents, setProjectEvents] = useState([]);
  const [reps, setReps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [followingUp, setFollowingUp] = useState(null); // entry being followed-up
  const [error, setError] = useState('');
  const [filter, setFilter] = useState({ status: '', rep_id: '', kind: '', sort: '' });
  // Rentalman customer list -- loaded once so the Base business "Customer"
  // field can offer an autocomplete picker. Plain text input with a datalist
  // so free-text still works for non-Rentalman names.
  const [customers, setCustomers] = useState([]);
  // Opportunities for the "Related opportunity" picker — that customer's
  // projects, fetched (debounced) when the entry form's customer changes.
  const [oppOptions, setOppOptions] = useState([]);
  // Schedule Event modal -- always create-mode from this entry point. Calendar
  // page is the canonical place to view / edit / respond to events.
  const [showEventModal, setShowEventModal] = useState(false);
  const isMobile = useIsMobile();

  const reload = () => {
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.rep_id) params.rep_id = filter.rep_id;
    if (filter.kind) params.kind = filter.kind;
    if (filter.sort) params.sort = filter.sort;
    api.listCallLog(params).then(setEntries).catch(e => setError(e.message));
    // Load project events in parallel. Suppressed when kind=Base (events are
    // always Project-kind). Status filter doesn't apply to events (they don't
    // have a pipeline status -- they're change records).
    if (filter.kind !== 'Base') {
      const evtParams = {};
      if (filter.rep_id) evtParams.rep_id = filter.rep_id;
      if (filter.kind) evtParams.kind = filter.kind;
      api.listProjectEvents(evtParams).then(setProjectEvents).catch(() => setProjectEvents([]));
    } else {
      setProjectEvents([]);
    }
  };

  useEffect(reload, [filter.status, filter.rep_id, filter.kind, filter.sort]);

  // Load the customer's opportunities for the picker whenever the entry form's
  // customer changes (debounced so it doesn't fire on every keystroke).
  useEffect(() => {
    const cust = editing?.customer?.trim();
    if (!cust) { setOppOptions([]); return; }
    const t = setTimeout(() => {
      api.activityOpportunities(cust).then(setOppOptions).catch(() => setOppOptions([]));
    }, 400);
    return () => clearTimeout(t);
  }, [editing?.customer]);
  useEffect(() => {
    if (isAdminLike(user) || user.role === 'supervisor') {
      api.listReps().then(setReps).catch(() => {});
    }
    // Load the Rentalman customer master for the Base-business customer picker.
    // Active customers only -- status='A' keeps the list manageable. Every
    // non-ops role sees the full list now so this is safe for all reps.
    api.listCustomers({ status: 'A', limit: 5000 })
      .then(rows => setCustomers(rows.map(r => r.name).sort((a, b) => a.localeCompare(b))))
      .catch(() => {});
  }, [user.role]);

  const scrollToForm = () => setTimeout(() =>
    document.getElementById('call-log-edit-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
  50);

  const startCreate = () => { setEditing(blank()); scrollToForm(); };
  const startEdit = (e) => {
    setEditing({ ...e, week_of: e.week_of?.slice(0,10), follow_up_date: e.follow_up_date?.slice(0,10) || '' });
    scrollToForm();
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    setError('');
    try {
      const payload = { ...editing };
      if (!payload.follow_up_date) payload.follow_up_date = null;
      if (!payload.rep_id) payload.rep_id = user.id;
      if (editing.id) await api.updateCallLog(editing.id, payload);
      else await api.createCallLog(payload);
      setEditing(null);
      reload();
    } catch (err) { setError(err.message); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this entry?')) return;
    await api.deleteCallLog(id);
    reload();
  };

  // Required fields enforced on NEW entries only. Existing rows imported
  // before this rule (or pre-launch) can be edited without re-filling.
  //   Cold Call relaxes contact name/title/email/phone -- walk-ins
  //     without a known person are still loggable.
  //   Closed + Lost statuses skip the follow-up-date requirement -- the
  //     deal is terminal, no follow-up needed.
  //   kind (Base / Project) must be picked explicitly -- no auto-default.
  const REQUIRED_BASE = ['week_of', 'customer', 'contact_type', 'notes', 'kind'];
  const REQUIRED_CONTACT_FIELDS = ['contact_name', 'contact_title', 'email', 'phone'];
  const REQUIRED_LABELS = {
    week_of: 'Week of', customer: 'Customer', contact_name: 'Contact Name',
    contact_title: 'Contact Title', contact_type: 'Contact Type',
    email: 'Email', phone: 'Phone', notes: 'Notes',
    follow_up_date: 'Follow-up Date', kind: 'Activity kind (Base or Project)'
  };
  const requiredFor = (e) => {
    const list = [...REQUIRED_BASE];
    if (!CONTACT_TYPES_NO_NAME_REQUIRED.has(e?.contact_type)) {
      list.push(...REQUIRED_CONTACT_FIELDS);
    }
    if (STATUSES_NEEDING_FOLLOWUP.has(e?.status)) {
      list.push('follow_up_date');
    }
    return list;
  };
  const missingRequired = editing && !editing.id
    ? requiredFor(editing).filter(f => !editing[f] || !String(editing[f]).trim())
    : [];

  // Supervisor+ flag: ticks an entry for follow-up with the rep. Toggles
  // server-side; entry surfaces on dashboard until cleared.
  const toggleFlag = async (entry) => {
    try {
      await api.flagCallLog(entry.id, !entry.supervisor_flag);
      reload();
    } catch (err) { setError(err.message); }
  };
  const canFlag = user.role !== 'rep';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ ...styles.h1, margin: 0 }}>Activity Log</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdminLike(user) && (
            <button style={styles.btnSecondary} onClick={async () => {
              // Export the current filtered view as xlsx (admin only).
              // Mirrors the Project Tracker export.
              const params = {};
              if (filter.status) params.status = filter.status;
              if (filter.rep_id) params.rep_id = filter.rep_id;
              if (filter.kind) params.kind = filter.kind;
              try {
                const res = await api.exportCallLog(params);
                if (!res.ok) throw new Error('Export failed');
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const stamp = new Date().toISOString().slice(0, 10);
                a.download = `sss-crm-activity-log-${stamp}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) { setError(err.message); }
            }}>↓ Export</button>
          )}
          {user.role !== 'ops' && (
            <button
              style={styles.btnSecondary}
              onClick={() => setShowEventModal(true)}
              title="Schedule a luncheon, dinner, golf, trade show, etc. (creates a calendar event)"
            >📅 Schedule Event</button>
          )}
          <button style={styles.btn} onClick={startCreate}>+ New Entry</button>
        </div>
      </div>

      {/* Mobile floating action button -- one-tap new entry from the truck */}
      <button
        onClick={startCreate}
        title="New entry"
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 30,
          width: 56, height: 56, borderRadius: '50%',
          background: '#E36F2C', color: '#fff', border: 'none',
          fontSize: 28, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
        }}
        aria-label="Add new call log entry"
      >
        +
      </button>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select style={{ ...styles.input, width: 180 }} value={filter.kind} onChange={e => setFilter({ ...filter, kind: e.target.value })}>
          <option value="">All activity</option>
          <option value="Base">Base business</option>
          <option value="Project">Project</option>
        </select>
        <select style={{ ...styles.input, width: 180 }} value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}>
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(isAdminLike(user) || user.role === 'supervisor') && (
          <select style={{ ...styles.input, width: 240 }} value={filter.rep_id} onChange={e => setFilter({ ...filter, rep_id: e.target.value })}>
            <option value="">All account managers</option>
            {reps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
          </select>
        )}
        {/* Sort: defaults to week_of (the activity period). 'Date entered'
            puts newest submissions on top -- handy for the weekly sales
            meeting review. */}
        <select style={{ ...styles.input, width: 220 }} value={filter.sort} onChange={e => setFilter({ ...filter, sort: e.target.value })}>
          <option value="">Sort: Week of (default)</option>
          <option value="created_at">Sort: Date entered (newest)</option>
          <option value="updated_at">Sort: Last edited</option>
        </select>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {editing && (
        <div id="call-log-edit-form" style={styles.card}>
          <h2 style={styles.h2}>{editing.id ? 'Edit Entry' : 'New Entry'}</h2>
          {/* Base vs Project -- first decision on the form, REQUIRED, no
              auto-default. Reps were missing the toggle when one was
              pre-selected; forcing a click ensures conscious tagging. */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ ...styles.label, marginBottom: 6 }}>
              Activity kind * {!editing.kind && <span style={{ color: '#92400E', fontWeight: 400, fontSize: 11 }}>← pick one</span>}
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['Base', 'Project'].map(k => {
                const active = editing.kind === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setEditing({ ...editing, kind: k })}
                    style={{
                      flex: '0 0 auto',
                      padding: '8px 18px',
                      borderRadius: 8,
                      border: `1.5px solid ${active ? '#E36F2C' : (!editing.kind ? '#F59E0B' : '#E5E7EB')}`,
                      background: active ? '#E36F2C' : '#fff',
                      color: active ? '#fff' : '#374151',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer'
                    }}
                  >
                    {k === 'Base' ? 'Base business' : 'Project'}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={styles.formGrid}>
            <Field label="Week of *">
              <input type="date" style={styles.input} value={editing.week_of || ''} onChange={e => setEditing({ ...editing, week_of: e.target.value })} />
            </Field>
            <Field label="Customer *">
              {/* Base business: autocomplete from the Rentalman customer master.
                  datalist keeps free-text as the fallback (non-Rentalman names,
                  project site names on Project entries, legacy data all still work).
                  Only shown for Base entries since Project entries use site names. */}
              {editing.kind === 'Base' && customers.length > 0 && (
                <datalist id="cl-customer-list">
                  {customers.map(n => <option key={n} value={n} />)}
                </datalist>
              )}
              <input
                style={styles.input}
                list={editing.kind === 'Base' && customers.length > 0 ? 'cl-customer-list' : undefined}
                value={editing.customer}
                placeholder={editing.kind === 'Base' ? 'Type to search Rentalman customers…' : 'Site / customer name'}
                onChange={e => setEditing({ ...editing, customer: e.target.value })}
              />
              {editing.kind === 'Base' && customers.length > 0 && (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>
                  Showing {customers.length.toLocaleString()} active Rentalman customers. Type any name for a new one.
                </div>
              )}
            </Field>
            <Field label={CONTACT_TYPES_NO_NAME_REQUIRED.has(editing.contact_type) ? 'Contact Name' : 'Contact Name *'}>
              <input style={styles.input} value={editing.contact_name || ''} onChange={e => setEditing({ ...editing, contact_name: e.target.value })} />
            </Field>
            <Field label={CONTACT_TYPES_NO_NAME_REQUIRED.has(editing.contact_type) ? 'Contact Title' : 'Contact Title *'}>
              <input style={styles.input} value={editing.contact_title || ''} onChange={e => setEditing({ ...editing, contact_title: e.target.value })} />
            </Field>
            <Field label={CONTACT_TYPES_NO_NAME_REQUIRED.has(editing.contact_type) ? 'Email' : 'Email *'}>
              <input type="email" style={styles.input} placeholder="contact@customer.com" value={editing.email || ''} onChange={e => setEditing({ ...editing, email: e.target.value })} />
            </Field>
            <Field label={CONTACT_TYPES_NO_NAME_REQUIRED.has(editing.contact_type) ? 'Phone' : 'Phone *'}>
              <input type="tel" style={styles.input} placeholder="(361) 555-1234" value={editing.phone || ''} onChange={e => setEditing({ ...editing, phone: e.target.value })} />
            </Field>
            <Field label={STATUSES_NEEDING_FOLLOWUP.has(editing.status) ? 'Follow-up Date *' : 'Follow-up Date'}>
              <input type="date" style={styles.input} value={editing.follow_up_date || ''} onChange={e => setEditing({ ...editing, follow_up_date: e.target.value })} />
              {!STATUSES_NEEDING_FOLLOWUP.has(editing.status) && (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  Not required for {editing.status} status.
                </div>
              )}
            </Field>
            <Field label="Follow-up Type">
              <input style={styles.input} placeholder="Lunch, Golf, Call..." value={editing.follow_up_type || ''} onChange={e => setEditing({ ...editing, follow_up_type: e.target.value })} />
            </Field>
            <Field label="Contact Type *">
              <select style={styles.input} value={editing.contact_type || ''} onChange={e => setEditing({ ...editing, contact_type: e.target.value })}>
                <option value="">— pick one —</option>
                {CONTACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select style={styles.input} value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Related opportunity">
              <select
                style={styles.input}
                value={editing.project_id || ''}
                onChange={e => setEditing({ ...editing, project_id: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">— none —</option>
                {/* Keep the currently-linked opportunity selectable even if it
                    isn't in the freshly-fetched list (customer text differs). */}
                {editing.project_id && !oppOptions.some(o => o.id === editing.project_id) && (
                  <option value={editing.project_id}>
                    {editing.project_site || `Opportunity #${editing.project_id}`}{editing.project_status ? ` · ${editing.project_status}` : ''}
                  </option>
                )}
                {oppOptions.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.site} · {o.status}{o.estimated_value ? ` · $${Math.round(Number(o.estimated_value)).toLocaleString()}` : ''}
                  </option>
                ))}
              </select>
              {!editing.customer?.trim() ? (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>Enter a customer to see their opportunities.</div>
              ) : oppOptions.length === 0 ? (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>No opportunities found for this customer.</div>
              ) : (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>Attachments will also show on the linked opportunity.</div>
              )}
            </Field>
            {isAdminLike(user) && (
              <Field label="Rep (Leadership only)">
                <select style={styles.input} value={editing.rep_id || user.id} onChange={e => setEditing({ ...editing, rep_id: Number(e.target.value) })}>
                  {reps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
                </select>
              </Field>
            )}
          </div>
          <Field label="Notes *">
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#6B7280', alignSelf: 'center', marginRight: 4 }}>Templates:</span>
              {NOTE_TEMPLATES.map(t => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => setEditing(prev => ({
                    ...prev,
                    notes: (prev.notes ? prev.notes + '\n\n' : '') + t.body,
                    contact_type: prev.contact_type || t.type   // also set type if blank
                  }))}
                  style={{
                    background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 14,
                    padding: '4px 10px', fontSize: 12, cursor: 'pointer'
                  }}
                  title="Insert template + set Contact Type"
                >
                  {t.label}
                </button>
              ))}
            </div>
            <textarea style={styles.textarea} value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} />
          </Field>
          {missingRequired.length > 0 && (
            <div style={{ background: '#FEF3C7', color: '#92400E', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginTop: 8 }}>
              Fill these to submit: <strong>{missingRequired.map(f => REQUIRED_LABELS[f]).join(', ')}</strong>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              style={{ ...styles.btn, opacity: missingRequired.length ? 0.5 : 1, cursor: missingRequired.length ? 'not-allowed' : 'pointer' }}
              onClick={save}
              disabled={missingRequired.length > 0}
              title={missingRequired.length ? 'Fill in all required fields first' : 'Save entry'}
            >Save</button>
            <button style={styles.btnSecondary} onClick={cancel}>Cancel</button>
          </div>
          {/* Attachments: documents + saved emails. Needs a saved entry (id),
              so on a new entry we prompt to save first. */}
          {editing.id ? (
            <Attachments callLogId={editing.id} />
          ) : (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #E5E7EB', fontSize: 12, color: '#6B7280' }}>
              Save the entry to attach documents or emails.
            </div>
          )}
        </div>
      )}

      {isMobile ? (
        <div>
          {entries.length === 0 && <div style={mobile.emptyHint}>No entries.</div>}
          {entries.map(e => {
            const isBase = (e.kind || 'Project') === 'Base';
            const cardStyle = e.supervisor_flag
              ? { ...mobile.listItem, background: '#FEF3C7', borderColor: '#F59E0B' }
              : mobile.listItem;
            return (
              <div key={e.id} style={cardStyle}>
                <div style={mobile.listItemHeader}>
                  <Link to={`/accounts/${encodeURIComponent(e.customer)}`} style={{ ...mobile.listItemTitle, color: '#E36F2C', textDecoration: 'none' }}>
                    {e.customer}
                  </Link>
                  <StatusPill status={e.status} />
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                    background: isBase ? '#F3F4F6' : '#EEF2FF',
                    color: isBase ? '#374151' : '#3730A3'
                  }}>{isBase ? 'Base' : 'Project'}</span>
                  {e.contact_type && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#EEF2FF', color: '#3730A3' }}>
                      {(CONTACT_TYPES.find(t => t.value === e.contact_type)?.label) || e.contact_type}
                    </span>
                  )}
                </div>
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Week</span>
                  <span style={mobile.listValue}>{fmtDate(e.week_of)}</span>
                </div>
                {e.rep_name && (
                  <div style={mobile.listRow}>
                    <span style={mobile.listLabel}>Acct Mgr</span>
                    <span style={mobile.listValue}>{e.rep_name}</span>
                  </div>
                )}
                {(e.contact_name || e.contact_title) && (
                  <div style={mobile.listRow}>
                    <span style={mobile.listLabel}>Contact</span>
                    <span style={mobile.listValue}>
                      {e.contact_name}{e.contact_title ? <span style={{ color: '#6B7280' }}> · {e.contact_title}</span> : ''}
                    </span>
                  </div>
                )}
                {(e.follow_up_date || e.follow_up_type) && (
                  <div style={mobile.listRow}>
                    <span style={mobile.listLabel}>Follow-up</span>
                    <span style={mobile.listValue}>
                      {fmtDate(e.follow_up_date)}{e.follow_up_type ? <span style={{ color: '#6B7280' }}> · {e.follow_up_type}</span> : ''}
                    </span>
                  </div>
                )}
                {e.notes && (
                  <div style={{ ...mobile.listRow, flexDirection: 'column', alignItems: 'stretch' }}>
                    <span style={mobile.listLabel}>Notes</span>
                    <span style={{ ...mobile.listValue, textAlign: 'left', whiteSpace: 'pre-wrap', marginTop: 4 }}>{e.notes}</span>
                  </div>
                )}
                <div style={mobile.listActions}>
                  <button style={{ ...styles.btn, ...mobile.btnTap, flex: 1 }} onClick={() => setFollowingUp(e)}>+ Follow-up</button>
                  <button style={{ ...styles.btnSecondary, ...mobile.btnTap }} onClick={() => startEdit(e)}>Edit</button>
                  <button style={{ ...styles.btnDanger, ...mobile.btnTap }} onClick={() => remove(e.id)}>Del</button>
                  {canFlag && (
                    <button
                      onClick={() => toggleFlag(e)}
                      title={e.supervisor_flag ? 'Clear follow-up flag' : 'Flag for follow-up with rep'}
                      style={{
                        ...mobile.btnTap, flexBasis: '100%',
                        background: e.supervisor_flag ? '#FEF3C7' : '#fff',
                        border: `1px solid ${e.supervisor_flag ? '#92400E' : '#E5E7EB'}`,
                        color: e.supervisor_flag ? '#92400E' : '#6B7280',
                        borderRadius: 6, fontWeight: 600, cursor: 'pointer'
                      }}
                    >
                      {e.supervisor_flag ? '⚑ Flagged for review' : '⚐ Flag for review'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {/* Mobile project event cards */}
          {projectEvents.length > 0 && (
            <div style={{ ...mobile.listItem, background: '#F5F3FF', border: '1px solid #C7D2FE', marginTop: 8, borderLeft: '3px solid #6366F1' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#3730A3', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                📊 Project Tracker Updates
              </div>
              {projectEvents.map(ev => (
                <div key={`evt-${ev.id}`} style={{ paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid #E0E7FF' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#E36F2C' }}>{ev.site}</div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                    {TRACKED_FIELDS_LABEL[ev.field] || ev.field}:{' '}
                    {ev.old_value && <span style={{ textDecoration: 'line-through', marginRight: 4, color: '#9CA3AF' }}>{ev.old_value}</span>}
                    <strong style={{ color: '#1F2937' }}>{ev.new_value || '(cleared)'}</strong>
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                    {fmtDate(ev.changed_at)} · by {ev.changed_by_name || 'system'} · {ev.account_owner_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Week</th>
              <th style={styles.th}>Kind</th>
              <th style={styles.th}>Account Mgr</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Contact</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Notes</th>
              <th style={styles.th}>Follow-up</th>
              <th style={styles.th}>Status</th>
              {canFlag && <th style={styles.th}>Flag</th>}
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={canFlag ? 11 : 10} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>No entries.</td></tr>
            )}
            {entries.map(e => {
              const isBase = (e.kind || 'Project') === 'Base';
              return (
              <tr key={e.id} style={e.supervisor_flag ? { background: '#FEF3C7' } : {}}>
                <td style={styles.td}>{fmtDate(e.week_of)}</td>
                <td style={styles.td}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                    background: isBase ? '#F3F4F6' : '#EEF2FF',
                    color: isBase ? '#374151' : '#3730A3',
                    whiteSpace: 'nowrap'
                  }}>
                    {isBase ? 'Base' : 'Project'}
                  </span>
                </td>
                <td style={styles.td}>{e.rep_name}</td>
                <td style={styles.td}>
                  <Link to={`/accounts/${encodeURIComponent(e.customer)}`} style={{ color: '#E36F2C', fontWeight: 600, textDecoration: 'none' }}>
                    {e.customer}
                  </Link>
                </td>
                <td style={styles.td}>
                  {e.contact_name}
                  {e.contact_title && <div style={{ color: '#6B7280', fontSize: 12 }}>{e.contact_title}</div>}
                  {e.email && <div style={{ color: '#6B7280', fontSize: 11 }}>{e.email}</div>}
                  {e.phone && <div style={{ color: '#6B7280', fontSize: 11 }}>{e.phone}</div>}
                </td>
                <td style={styles.td}>
                  {e.contact_type && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#EEF2FF', color: '#3730A3', whiteSpace: 'nowrap' }}>
                      {(CONTACT_TYPES.find(t => t.value === e.contact_type)?.label) || e.contact_type}
                    </span>
                  )}
                </td>
                <td style={{ ...styles.td, maxWidth: 380, whiteSpace: 'pre-wrap' }}>{e.notes}</td>
                <td style={styles.td}>{fmtDate(e.follow_up_date)}{e.follow_up_type ? <div style={{ color: '#6B7280', fontSize: 12 }}>{e.follow_up_type}</div> : null}</td>
                <td style={styles.td}><StatusPill status={e.status} /></td>
                {canFlag && (
                  <td style={styles.td}>
                    <button
                      onClick={() => toggleFlag(e)}
                      title={e.supervisor_flag ? 'Clear follow-up flag' : 'Flag for follow-up with rep'}
                      style={{
                        background: e.supervisor_flag ? '#FEF3C7' : '#fff',
                        border: `1px solid ${e.supervisor_flag ? '#92400E' : '#E5E7EB'}`,
                        color: e.supervisor_flag ? '#92400E' : '#6B7280',
                        padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 14
                      }}
                    >
                      {e.supervisor_flag ? '⚑ Flagged' : '⚐ Flag'}
                    </button>
                  </td>
                )}
                <td style={styles.td}>
                  <button style={styles.btn} onClick={() => setFollowingUp(e)} title="Add a dated follow-up note to this card">+ Follow-up</button>{' '}
                  <button style={styles.btnSecondary} onClick={() => startEdit(e)}>Edit</button>{' '}
                  <button style={styles.btnDanger} onClick={() => remove(e.id)}>Delete</button>
                </td>
              </tr>
              );
            })}
            {/* Project change events -- rendered as non-editable rows below
                the call log entries. Inline with the table so they appear
                in the same feed. Distinguished by an indigo left-border
                and a 📊 chip instead of a status pill. */}
            {projectEvents.length > 0 && (
              <tr>
                <td colSpan={canFlag ? 11 : 10} style={{ ...styles.td, paddingTop: 12, paddingBottom: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    📊 Project Tracker Updates
                  </div>
                </td>
              </tr>
            )}
            {projectEvents.map(ev => (
              <tr key={`evt-${ev.id}`} style={{ borderLeft: '3px solid #6366F1', background: '#F5F3FF' }}>
                <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}>{fmtDate(ev.changed_at)}</td>
                <td style={styles.td}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#EEF2FF', color: '#3730A3' }}>
                    Project
                  </span>
                </td>
                <td style={{ ...styles.td, fontSize: 12 }}>{ev.account_owner_name || '—'}</td>
                <td style={{ ...styles.td, fontWeight: 600, color: '#E36F2C' }}>{ev.site}</td>
                <td style={{ ...styles.td, fontSize: 12 }}>—</td>
                <td style={{ ...styles.td, fontSize: 12 }}>—</td>
                <td style={{ ...styles.td, fontSize: 12 }}>—</td>
                <td colSpan={2} style={{ ...styles.td, fontSize: 12 }}>
                  <span style={{ color: '#6B7280' }}>{TRACKED_FIELDS_LABEL[ev.field] || ev.field}: </span>
                  {ev.old_value && <span style={{ color: '#9CA3AF', textDecoration: 'line-through', marginRight: 4 }}>{ev.old_value}</span>}
                  <span style={{ color: '#1F2937', fontWeight: 600 }}>{ev.new_value || '(cleared)'}</span>
                  <span style={{ color: '#9CA3AF', fontSize: 11, marginLeft: 8 }}>by {ev.changed_by_name || 'system'}</span>
                </td>
                <td style={{ ...styles.td, fontSize: 11, color: '#6B7280' }}>
                  <span style={{ background: '#E0E7FF', color: '#3730A3', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                    Tracker update
                  </span>
                </td>
                {canFlag && <td style={styles.td} />}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {followingUp && (
        <FollowUpModal
          entry={followingUp}
          onCancel={() => setFollowingUp(null)}
          onSave={async (body) => {
            try {
              await api.addCallLogFollowUp(followingUp.id, body);
              setFollowingUp(null);
              reload();
            } catch (err) { setError(err.message); }
          }}
        />
      )}

      {showEventModal && (
        <EventModal
          event={null}
          currentUser={user}
          onClose={() => setShowEventModal(false)}
          onSaved={() => {}}
        />
      )}
    </>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label style={styles.label}>{label}</label>
    {children}
  </div>
);

// FollowUpModal moved to components/FollowUpModal.jsx (shared with the
// Dashboard's Follow-ups Due list).
